import { useEffect, useState } from "react";

import {
  ClipboardDocumentCheckIcon,
  TrashIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

import StockTable from "../../components/stock/StockTable";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import HeadlineStat from "../../components/shared/HeadlineStat";
import Tabs from "../../components/shared/Tabs/Tabs";
import useStockQcStats from "../../data/useStockQcStats";
import { PageMode } from "../../models/PagedTable";

const formatCount = (value: number | undefined) =>
  typeof value === "number" ? value.toLocaleString("en-GB") : "—";

export default function StockQc() {
  const { setInfo } = usePageNameContext();
  const { totals, qcCount, deletedCount, scrapOutCount } = useStockQcStats();
  const [viewType, setViewType] = useState(0);

  const viewTypes = [
    { value: 0, label: "Stock QC" },
    { value: 1, label: "Deleted" },
    { value: 2, label: "Scrap Out" },
  ];

  useEffect(() => {
    setInfo({
      name: "Stock QC",
      desc: "View and manage all stock that requires QC",
    });
  }, []);

  return (
    <>
      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <HeadlineStat
          value={formatCount(totals.qc)}
          label="In QC"
          sublabel={qcCount.isFetching ? "Refreshing…" : "Awaiting review"}
          tone="amber"
          icon={ClipboardDocumentCheckIcon}
          onClick={() => setViewType(0)}
          active={viewType === 0}
          title="Show parts currently in QC"
        />
        <HeadlineStat
          value={formatCount(totals.deleted)}
          label="Deleted"
          sublabel={deletedCount.isFetching ? "Refreshing…" : "Soft-deleted parts"}
          tone="rose"
          icon={TrashIcon}
          onClick={() => setViewType(1)}
          active={viewType === 1}
          title="Show deleted parts"
        />
        <HeadlineStat
          value={formatCount(totals.scrapOut)}
          label="Scrap Out"
          sublabel={scrapOutCount.isFetching ? "Refreshing…" : "Sent to scrap"}
          tone="slate"
          icon={XCircleIcon}
          onClick={() => setViewType(2)}
          active={viewType === 2}
          title="Show scrap-out parts"
        />
      </div>

      <div className="mt-3">
        <Tabs viewType={viewType} viewTypes={viewTypes} setViewType={setViewType} />
      </div>

      <StockTable
        showActions={false}
        tableKey={`advanced-search-qc-${viewType}`}
        tableUrl="/part/stock/qc"
        showQcReason
        pageMode={
          viewType === 0
            ? PageMode.Stock
            : viewType === 1
              ? PageMode.DeletedStock
              : PageMode.ScrapOutStock
        }
      />
    </>
  );
}
